import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Table,
    Input,
    Breadcrumb,
    Tag,
    DatePicker,
    Row,
    Col,
    Grid,
    message,
    Select,
} from 'antd';
import { Link } from 'react-router-dom';
import {
    RightOutlined,
    FileExcelOutlined,
    FilePdfOutlined,
    DownloadOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { walletService } from '../../common/services';
import moment from 'moment';
import 'moment/locale/fr';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/** Petit hook de debounce pour la recherche */
const useDebounce = (value, delay = 400) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(id);
    }, [value, delay]);
    return debounced;
};

const HistoriquePoints = () => {
    const [data, setData] = useState([]);
    const [isLoading, setLoading] = useState(false);

    // state
    const [isExporting, setIsExporting] = useState(false);


    // filtres
    const [phDateRange, setPhDateRange] = useState(null); // [dayjs/moment, dayjs/moment]
    const [phType, setPhType] = useState(null);           // string
    const [phQuery, setPhQuery] = useState('');           // string
    const phQueryDebounced = useDebounce(phQuery, 400);

    // pagination/tri côté serveur
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const [sorter, setSorter] = useState({ field: 'date', order: 'descend' }); // -date par défaut

    // breakpoints
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;
    const controlSize = isMobile ? 'small' : 'middle';

    const fetchAllPages = async (baseParams) => {
        const rows = [];
        let pageIndex = 1;
        const pageSize = 1000; // adapte si besoin (et côté backend)
        let total = Infinity;

        while (rows.length < total) {
            const res = await walletService.historiquePoints({
                ...baseParams,
                page: pageIndex,
                limit: pageSize,
            });
            const items = Array.isArray(res?.items) ? res.items : [];
            rows.push(...items);
            total = res?.total ?? rows.length;
            if (!items.length) break;
            pageIndex += 1;
            // sécurité anti-boucle infinie si totalPages dispo
            if (res?.totalPages && pageIndex > res.totalPages) break;
        }
        return rows;
    };

    const mapForExport = (rows) =>
        rows.map((it) => ({
            Type: it.type,
            Téléphone: formatPhoneNumber(it.phone),
            Description: it.description,
            Points: it.points,
            Date: moment(it.date).format('DD/MM/YYYY HH:mm:ss'),
        }));





    // params API calculés à partir de l'état
    const apiParams = useMemo(() => {
        const params = {
            page,
            limit,
            sort: sorter?.field
                ? (sorter.order === 'descend' ? `-${sorter.field}` : `${sorter.field}`)
                : '-date',
        };
        if (phType) params.type = phType;
        if (phQueryDebounced) params.q = phQueryDebounced.trim();

        if (phDateRange && phDateRange.length === 2 && phDateRange[0] && phDateRange[1]) {
            params.start = phDateRange[0].format('YYYY-MM-DD');
            params.end = phDateRange[1].format('YYYY-MM-DD'); // le backend inclut 23:59:59.999
        }
        return params;
    }, [page, limit, sorter, phType, phQueryDebounced, phDateRange]);

    // chargement (server-side)
    const load = async () => {
        try {
            setLoading(true);
            const res = await walletService.historiquePoints(apiParams);
            // res attendu: { items, total, totalPages, page, limit, sumPoints }
            setData(Array.isArray(res?.items) ? res.items : []);
            setTotal(res?.total || 0);

            // Si le serveur renvoie page/limit différents, on s'aligne
            if (res?.page && res.page !== page) setPage(res.page);
            if (res?.limit && res.limit !== limit) setLimit(res.limit);
        } catch (err) {
            console.error('Erreur API:', err);
            message.error("Impossible de charger l'historique des points.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiParams]); // recharge à chaque changement de params

    // formatage téléphone (Côte d'Ivoire)
    const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        let digits = String(phone).replace(/\D/g, '');
        if (digits.startsWith('225')) digits = digits.slice(3); // retire +225/225
        if (digits.length === 9) digits = '0' + digits; // normalise à 10 si besoin
        return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
    };

    // Exports (page courante). Pour exporter « tout », refaire un appel backend sans pagination.
    // const exportRows = data.map((it) => ({
    //     Type: it.type,
    //     Téléphone: formatPhoneNumber(it.phone),
    //     Description: it.description,
    //     Points: it.points,
    //     Date: moment(it.date).format('DD/MM/YYYY HH:mm:ss'),
    // }));

    const exportToExcel = async () => {
        try {
            setIsExporting(true);
            message.loading({ content: 'Préparation de l’export Excel…', key: 'exp' });
            const all = await fetchAllPages(apiParams);
            const rows = mapForExport(all);
            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Historique');
            XLSX.writeFile(wb, 'historique_points.xlsx');
            message.success({ content: 'Export Excel terminé.', key: 'exp' });
        } catch (e) {
            console.error(e);
            message.error({ content: 'Échec de l’export Excel.', key: 'exp' });
        } finally {
            setIsExporting(false);
        }
    };

    const exportToCSV = async () => {
        try {
            setIsExporting(true);
            message.loading({ content: 'Préparation de l’export CSV…', key: 'exp' });
            const all = await fetchAllPages(apiParams);
            const rows = mapForExport(all);
            const ws = XLSX.utils.json_to_sheet(rows);
            const csv = XLSX.utils.sheet_to_csv(ws);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            saveAs(blob, 'historique_points.csv');
            message.success({ content: 'Export CSV terminé.', key: 'exp' });
        } catch (e) {
            console.error(e);
            message.error({ content: 'Échec de l’export CSV.', key: 'exp' });
        } finally {
            setIsExporting(false);
        }
    };

    const exportToPDF = async () => {
        try {
            setIsExporting(true);
            message.loading({ content: 'Préparation du PDF…', key: 'exp' });
            const all = await fetchAllPages(apiParams);
            const doc = new jsPDF();
            doc.text('Historique des points', 14, 10);
            autoTable(doc, {
                startY: 20,
                head: [['Type', 'Téléphone', 'Description', 'Points', 'Date']],
                body: all.map((item) => [
                    item.type,
                    formatPhoneNumber(item.phone),
                    item.description,
                    String(item.points),
                    moment(item.date).format('DD/MM/YYYY HH:mm:ss'),
                ]),
            });
            doc.save('historique_points.pdf');
            message.success({ content: 'Export PDF terminé.', key: 'exp' });
        } catch (e) {
            console.error(e);
            message.error({ content: "Échec de l’export PDF.", key: 'exp' });
        } finally {
            setIsExporting(false);
        }
    };


    const columns = [
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (text) => <Tag color="blue">{text}</Tag>,
            sorter: true,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Téléphone',
            dataIndex: 'phone',
            key: 'phone',
            render: (phone) => formatPhoneNumber(phone),
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            responsive: ['sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Points',
            dataIndex: 'points',
            key: 'points',
            render: (points) => <Tag color={points < 0 ? 'red' : 'green'}>{points}</Tag>,
            sorter: true,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (text) => moment(text).format('DD/MM/YYYY HH:mm:ss'),
            sorter: true,
            defaultSortOrder: 'descend',
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
    ];

    return (
        <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="row">
                    <div className="col-md-12">
                        <Breadcrumb
                            separator={<RightOutlined style={{ fontSize: 16, color: '#999', fontWeight: 'bolder' }} />}
                            style={{ marginBottom: 16 }}
                        >
                            <Breadcrumb.Item>
                                <Link to="/dashboard" style={{ fontSize: 16, fontWeight: 'bolder' }}>
                                    🏠 ACCUEIL
                                </Link>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                <span style={{ fontSize: 16, fontWeight: 'bolder' }}>POINTS</span>
                            </Breadcrumb.Item>
                        </Breadcrumb>

                        <div className="card mb-4">
                            <div className="card-body">
                                {/* 🔎 Filtres */}
                                <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                                    <Col xs={24} md={10}>
                                        <DatePicker.RangePicker
                                            size={controlSize}
                                            style={{ width: '100%' }}
                                            format="DD/MM/YYYY"
                                            value={phDateRange}
                                            onChange={(dates) => {
                                                setPage(1);
                                                setPhDateRange(dates);
                                                setLoading(true); // spinner immédiat
                                            }}
                                            placeholder={['Date début', 'Date fin']}
                                        />
                                    </Col>

                                    <Col xs={24} md={6}>
                                        <Select
                                            size={controlSize}
                                            allowClear
                                            style={{ width: '100%' }}
                                            placeholder="Filtrer par type"
                                            value={phType}
                                            onChange={(val) => {
                                                setPage(1);
                                                setPhType(val);
                                                setLoading(true); // spinner immédiat
                                            }}
                                            options={[
                                                { value: 'points_rechargement', label: 'Points rechargement' },
                                                { value: 'sos_credit_montant', label: 'SOS crédit (montant)' },
                                                { value: 'sos_credit_frais', label: 'SOS crédit (frais)' },
                                                { value: 'bonus_dimanche', label: 'Bonus dimanche' },
                                                { value: 'transfert_uv', label: 'Transfert UV' },
                                                { value: 'maj_classe_service', label: 'Classe de service' },
                                                { value: 'lier_vehicule', label: 'Liaison véhicule au chauffeur' },
                                                { value: 'detacher_vehicule', label: 'Détacher véhicule au chauffeur' },
                                                { value: 'retrait_points_virement', label: 'Virement' },
                                                { value: 'bonus_130_heures', label: 'Bonus 130ieme Heure' },
                                                { value: 'bonus_55_courses', label: 'Bonus 55ieme Course' },
                                            ]}
                                        />
                                    </Col>

                                    <Col xs={24} md={8}>
                                        <Input
                                            size={controlSize}
                                            allowClear
                                            prefix={<SearchOutlined />}
                                            placeholder="Recherche (description, téléphone, type)"
                                            value={phQuery}
                                            onChange={(e) => {
                                                setPage(1);
                                                setPhQuery(e.target.value);
                                                setLoading(true); // spinner immédiat
                                            }}
                                        />
                                    </Col>
                                </Row>

                                {/* ⬇️ Boutons d’export */}
                                <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={6}>
                                        <Button
                                            block={isMobile}
                                            size={controlSize}
                                            icon={<FileExcelOutlined />}
                                            onClick={exportToExcel}
                                            type="primary"
                                             disabled={isExporting}
                                        >
                                            Exporter Excel (.xlsx)
                                        </Button>
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={6}>
                                        <Button
                                            block={isMobile}
                                            size={controlSize}
                                            icon={<DownloadOutlined />}
                                            onClick={exportToCSV}
                                            disabled={isExporting}
                                        >
                                            Exporter CSV
                                        </Button>
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={6}>
                                        <Button
                                            block={isMobile}
                                            size={controlSize}
                                            icon={<FilePdfOutlined />}
                                            onClick={exportToPDF}
                                            disabled={isExporting}
                                            danger
                                        >
                                            Exporter PDF
                                        </Button>
                                    </Col>
                                </Row>

                                {/* 🧾 Table (spinner intégré) */}
                                <Table
                                    loading={isLoading ? { tip: 'Chargement des résultats…' } : false}
                                    size={isMobile ? 'small' : 'middle'}
                                    columns={columns}
                                    dataSource={data}
                                    rowKey={(record) => record._id || `${record.phone}-${record.date}`}
                                    pagination={{
                                        current: page,
                                        pageSize: limit,
                                        total,
                                        hideOnSinglePage: isMobile,
                                        showSizeChanger: !isMobile,
                                    }}
                                    onChange={(pagination, _filters, sorterArg) => {
                                        setLoading(true); // spinner immédiat
                                        setPage(pagination.current);
                                        setLimit(pagination.pageSize);
                                        const s = Array.isArray(sorterArg) ? sorterArg[0] : sorterArg;
                                        if (s && s.field) {
                                            setSorter({ field: s.field, order: s.order });
                                        } else {
                                            // si l'utilisateur reset le tri
                                            setSorter({ field: 'date', order: 'descend' });
                                        }
                                    }}
                                    scroll={{ x: true }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HistoriquePoints;
